import React, { useEffect, useState, useMemo, useCallback } from "react";
import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Plus } from "lucide-react";
import Select, { components, OptionProps } from "react-select";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";
import { Contact, Conversation } from "../../Types"; // Assuming Contact type is correctly defined here
import ContactCreateModal from "../ContactCreate";

// Helper hook for debouncing
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// 2. Custom Loading Message
const CustomLoadingMessage: React.FC<any> = (props) => (
  <components.LoadingMessage {...props}>
    <div className="text-center p-2 text-sm text-gray-500">
      Loading contacts...
    </div>
  </components.LoadingMessage>
);

// --- Main Component ---

interface propTypes {
  handleConversationClick: (conversation: Conversation) => void;
  conversations: Conversation[];
}

const ConversationCreateModal = ({
  handleConversationClick,
  conversations,
}: propTypes) => {
  const AppService = useAppServices();
  const { locationId } = useParams();
  const [show, setShow] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  // Use the actual Contact type if it's correct, otherwise use any[]
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Debounce the search term to avoid hitting the API on every keystroke
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const toggle = () => setShow((prev) => !prev);

  // Function to build the query string for API
  const buildContactQueryString = () => {
    const params = new URLSearchParams();

    if (!locationId) return ""; // Prevent call without locationId

    params.append("locationId", locationId);
    if (debouncedSearchTerm) {
      params.append("search", debouncedSearchTerm);
    }

    return params.toString();
  };

  // Function to fetch contacts
  const getContacts = async () => {
    if (!locationId) {
      console.warn("Cannot fetch contacts without locationId.");
      return;
    }

    setLoading(true);
    try {
      const query = buildContactQueryString();
      const { response } = await AppService.contact.filter({
        query: query,
      });

      if (response && response.data) {
        // Ensure response.data is treated as an array of Contact
        setContacts(response.data as Contact[]);
      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  let pageLoad = true;
  useEffect(() => {
    if (pageLoad) {
      getContacts();
      pageLoad = false;
    }
  }, [debouncedSearchTerm]);

  const contactOptions = useMemo(
    () =>
      contacts.map((contact) => ({
        label: `${contact?.firstName} ${contact?.lastName}`,
        value: contact?._id,
      })),
    [contacts]
  );

  // Validation Schema
  const validationSchema = Yup.object().shape({
    contactId: Yup.string().required("Please select a contact"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      contactId: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
        locationId: locationId,
      };
      const finConv = conversations.find(
        (item) => item.contactId?._id == values.contactId
      );
      if (finConv) {
        formik.resetForm();
        toggle();
        handleConversationClick(finConv);
      } else {
        try {
          const { response } = await AppService.conversation.create({
            payload,
            toaster: true,
          });
          if (response) {
            formik.resetForm();
            toggle();
            handleConversationClick(response.data);
          }
        } catch (error) {
          console.error("Submission error:", error);
        }
      }
    },
  });

  // Get the currently selected value for the Select component
  const selectedContact = contactOptions.find(
    (contact) => contact.value === formik.values.contactId
  );

  return (
    <>
      <button
        className="inline-flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md shrink-0 bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500"
        onClick={toggle}
      >
        <Plus className="size-4 mx-auto" />
      </button>
      <Modal
        show={show}
        onHide={toggle}
        id="contactModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[35rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20">
          <Modal.Title className="text-16 font-semibold">
            Create Conversation
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4 max-h-[calc(100vh-180px)] overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium">
                Select Contact
              </label>
              <Select
                name="contactId"
                options={contactOptions}
                placeholder={
                  loading ? "Loading..." : "Select or search for a Contact"
                }
                value={selectedContact}
                isLoading={loading} // Use isLoading prop for built-in loader
                // ** ⭐️ New Search Implementation **
                onInputChange={(newValue) => setSearchTerm(newValue)}
                isSearchable={true}
                components={{
                  NoOptionsMessage: (props) => (
                    <div className="text-center p-2">
                      {searchTerm ? (
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                          No contacts found for "<strong>{searchTerm}</strong>".
                        </p>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                          Start typing to search for a contact.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowContactModal(true)} // Trigger modal state
                        className="w-full text-blue-500 bg-blue-50 border border-blue-200 py-1.5 px-3 rounded-md hover:bg-blue-100 transition-colors text-sm font-medium"
                      >
                        + Create New Contact
                      </button>
                    </div>
                  ),
                  LoadingMessage: CustomLoadingMessage,
                }}
                // End of New Search Implementation

                onChange={(option) =>
                  formik.setFieldValue("contactId", option?.value ?? "")
                }
                onBlur={() => formik.setFieldTouched("contactId", true)}
              />
              {formik.touched.contactId && formik.errors.contactId && (
                <p className="text-red-400 text-sm mt-1">
                  {formik.errors.contactId}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 bg-white border btn hover:text-red-600"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <ContactCreateModal
        show={showContactModal}
        toggle={() => setShowContactModal(!showContactModal)}
        searchTerm={searchTerm}
        handleRefresh={() => {
          formik.resetForm();
          toggle();
        }}
        handleConversationClick={handleConversationClick}
      />
    </>
  );
};

export default ConversationCreateModal;
